import React, { useEffect, useState } from 'react';
import './PlotReadyOfferCard.css';

/**
 * Plot Workshop Work-this-beat Accept (#995).
 * Soft offer: Use this for [Beat] / Edit / Keep talking.
 * Accept writes the blurb into that Framework slot — no copy-paste.
 */

export const BEAT_WORKSHOP_KEEP_TALKING = 'Keep going';
export const BEAT_WORKSHOP_EDIT = 'Edit';
export const BEAT_WORKSHOP_REPLACE_ASK = 'Replace the current write-up for this sequence?';
export const BEAT_WORKSHOP_REPLACE = 'Replace';
export const BEAT_WORKSHOP_KEEP = 'Keep the current write-up';

const declineListeners = new Set();

export function thisForBeatLabel(beatName) {
  const name = String(beatName || '').trim() || 'this sequence';
  return `Use this for ${name}`;
}

export function attachBeatWorkshopOffer(message, offer) {
  if (!message || !offer?.offered) return message;
  return {
    ...message,
    metadata: { ...(message.metadata || {}), beat_workshop_offer: offer },
  };
}

export function declineStorageKey(sessionId, messageId) {
  return `brainstormy.beatWorkshopOffer.declined.${sessionId}.${messageId || 'latest'}`;
}

export function subscribeBeatWorkshopOfferDecline(listener) {
  declineListeners.add(listener);
  return () => declineListeners.delete(listener);
}

export function isBeatWorkshopOfferDeclined(sessionId, messageId) {
  if (!sessionId) return false;
  try {
    return window.localStorage.getItem(declineStorageKey(sessionId, messageId)) === '1';
  } catch {
    return false;
  }
}

export function markBeatWorkshopOfferDeclined(sessionId, messageId) {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(declineStorageKey(sessionId, messageId), '1');
  } catch {
    /* private mode / quota */
  }
  declineListeners.forEach((listener) => {
    listener(sessionId, messageId);
  });
}

export function shouldShowBeatWorkshopOffer(offer, sessionId, messageId) {
  if (!offer?.offered) return false;
  if (isBeatWorkshopOfferDeclined(sessionId, messageId)) return false;
  return true;
}

export function sceneRowsFromOffer(offer) {
  const rows = Array.isArray(offer?.scenes) ? offer.scenes : [];
  return rows
    .map((scene) => ({
      name: String(scene?.name || '').trim(),
      description: String(scene?.description || '').trim(),
    }))
    .filter((scene) => scene.name || scene.description);
}

export function renderSceneStubText(scenes) {
  return (scenes || [])
    .map((scene) => `${scene.name} — ${scene.description}`)
    .filter((line) => line.trim() !== '—')
    .join('\n');
}

export default function BeatWorkshopOfferCard({
  sessionId,
  messageId,
  offer,
  draft = '',
  onAccept,
  onDecline,
}) {
  const beatName = String(offer?.beat_name || '').trim();
  const [hidden, setHidden] = useState(() => isBeatWorkshopOfferDeclined(sessionId, messageId));
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft || '');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    setText(draft || '');
  }, [draft]);

  useEffect(() => {
    if (isBeatWorkshopOfferDeclined(sessionId, messageId)) {
      setHidden(true);
    }
    return subscribeBeatWorkshopOfferDecline((id, declinedMessageId) => {
      if (id === sessionId && declinedMessageId === messageId) setHidden(true);
    });
  }, [sessionId, messageId]);

  if (hidden) return null;

  const hide = () => {
    markBeatWorkshopOfferDeclined(sessionId, messageId);
    setHidden(true);
    onDecline?.();
  };

  const submit = async ({ replace = false } = {}) => {
    const content = (text || draft || '').trim();
    if (!content) {
      setError('Write something for this sequence first.');
      return;
    }
    if ((offer?.has_existing_blurb || confirming) && !replace) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAccept?.({
        beatName,
        content,
        replace: replace || confirming || Boolean(offer?.has_existing_blurb),
      });
      markBeatWorkshopOfferDeclined(sessionId, messageId);
      setHidden(true);
    } catch (err) {
      if (err?.status === 409) {
        setConfirming(true);
        setBusy(false);
        return;
      }
      setError(err?.message || 'Could not save this sequence.');
      setBusy(false);
    }
  };

  return (
    <div className="plot-ready-offer" role="group" aria-label={`Use this for ${beatName || 'this sequence'}`}>
      <div className="plot-ready-offer__lead">
        <span className="plot-ready-offer__hint">
          {confirming
            ? BEAT_WORKSHOP_REPLACE_ASK
            : `Ready to put this on ${beatName || 'this sequence'}?`}
        </span>
        <button
          type="button"
          className="plot-ready-offer__dismiss"
          onClick={hide}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>
      {editing && !confirming ? (
        <textarea
          className="plot-ready-offer__edit"
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={4}
          aria-label={`Edit write-up for ${beatName || 'this sequence'}`}
        />
      ) : null}
      <div className="plot-ready-offer__doors">
        {confirming ? (
          <>
            <button
              type="button"
              className="plot-ready-offer__door"
              disabled={busy}
              onClick={() => submit({ replace: true })}
            >
              {BEAT_WORKSHOP_REPLACE}
            </button>
            <button
              type="button"
              className="plot-ready-offer__skip"
              disabled={busy}
              onClick={() => setConfirming(false)}
            >
              {BEAT_WORKSHOP_KEEP}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="plot-ready-offer__door"
              disabled={busy}
              onClick={() => submit()}
            >
              {thisForBeatLabel(beatName)}
            </button>
            <button
              type="button"
              className="plot-ready-offer__skip"
              disabled={busy}
              onClick={() => setEditing((value) => !value)}
            >
              {BEAT_WORKSHOP_EDIT}
            </button>
            <button
              type="button"
              className="plot-ready-offer__skip"
              disabled={busy}
              onClick={hide}
            >
              {BEAT_WORKSHOP_KEEP_TALKING}
            </button>
          </>
        )}
      </div>
      {error ? <p className="plot-ready-offer__hint" role="alert">{error}</p> : null}
    </div>
  );
}
